import { useMemo } from "react";
import { Autocomplete } from "@mui/material";
import { useField } from "formik";
import { useSchoolLocal } from "../useSchoolLocal";
import { COUNTRY_CODES } from "./countryCodes";
import { FieldShell, fieldInputClassName } from "./FieldShell";
import type { Keys } from "./formik";

type Country = {
  code: string;
  name: string;
};

type CountrySelectProps = {
  name: Keys;
  label: string;
};

const ChevronDownIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="pointer-events-none absolute end-3.5 top-1/2 -translate-y-1/2 text-[#828282]"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Matches the form's inputs: 12px corners, soft shadow, inherited font.
const dropdownPaperSx = {
  mt: 0.75,
  borderRadius: "12px",
  boxShadow: "0 12px 32px rgba(26, 31, 54, 0.12)",
  fontFamily: "inherit",
  fontSize: "15px",
  color: "#2E2E38",
  "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "#EEEDFD" },
  "& .MuiAutocomplete-option[aria-selected='true']": {
    backgroundColor: "#EEEDFD",
    color: "#4242CE",
    fontWeight: 600,
  },
};

// Searchable country picker: MUI handles filtering and keyboard navigation, the input keeps our styling.
export const CountrySelect = ({ name, label }: CountrySelectProps) => {
  const { t, language } = useSchoolLocal();
  const [field, , helpers] = useField<string>(name);

  // Country names in the page language, sorted alphabetically for that language.
  const countries = useMemo<Country[]>(() => {
    const displayNames = new Intl.DisplayNames([language], { type: "region" });
    return COUNTRY_CODES.map((code) => ({
      code,
      name: displayNames.of(code) ?? code,
    })).sort((first, second) =>
      first.name.localeCompare(second.name, language),
    );
  }, [language]);

  const selectedCountry =
    countries.find((country) => country.code === field.value) ?? null;

  return (
    <FieldShell name={name} label={label} required>
      <Autocomplete
        id={name}
        // Rendered inside the form so the list inherits the page's text direction.
        disablePortal
        options={countries}
        value={selectedCountry}
        onChange={(_, country) => helpers.setValue(country?.code ?? "")}
        onBlur={() => helpers.setTouched(true)}
        getOptionLabel={(country) => country.name}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        noOptionsText={t("connect.noCountryFound")}
        componentsProps={{ paper: { sx: dropdownPaperSx } }}
        renderInput={(params) => (
          <div ref={params.InputProps.ref} className="relative">
            <input
              {...params.inputProps}
              // Chrome ignores "off" on address-like fields; "new-password" stops its autofill popup.
              autoComplete="new-password"
              placeholder={t("connect.selectCountry")}
              className={`${fieldInputClassName} pe-10`}
            />
            <ChevronDownIcon />
          </div>
        )}
      />
    </FieldShell>
  );
};
